import { useEffect, useMemo } from 'react';

import { useSnackbar } from 'notistack';
import { type AnyVariables, type UseQueryState } from 'urql';

import type * as types from './types';

import { useSelectedRole } from '@/src/features/auth/hooks';
import { useHasFeatureFlag } from '@/src/hooks/featureFlags/useHasFeatureFlag';
import { useLogger } from '@/src/hooks/internal/useLogger';

import { isNotAuthenticatedError } from '../client';
import * as constants from '../constants';
import { UrqlClientError } from '../errors';

type Exact<U extends types.UrqlQueryHook, O extends types.QueryHookFactoryOptions<U>> = {
  [key in keyof O & keyof types.QueryHookFactoryOptions<U>]?: O[key];
};

type WrappedQueryHook<U extends types.UrqlQueryHook, O extends types.QueryHookFactoryOptions<U>> = (
  variables: types.InferredQueryVariables<U>,
  options?: Omit<types.InferredQueryOptions<U>, 'variables'>,
) => types.WrappedQueryHookReturn<U, O>;

/**
 * A dummy feature flag return value that is used by the {@link useDummyFeatureFlag} hook when
 * feature flag usage is disabled for a created query hook.
 */
const FeatureFlagUnsafe = '__FEATURE_FLAG_UNSAFE_DO_NOT_USE__' as const;
type FeatureFlagUnsafe = typeof FeatureFlagUnsafe;

const useDummyFeatureFlag = () => FeatureFlagUnsafe;

/**
 * Returns the hook function that should be used to determine whether or not a given feature flag,
 * {@link FeatureFlagId}, is enabled or disabled, returning a dummy hook if feature flag usage
 * for the created query hook is disabled via the 'disableFeatureFlagUsage' option.
 *
 * This method is used to avoid issues with the conditional usage of hooks while still ensuring
 * that the {@link useFeatureFlag} hook is not called in cases where feature flag usage is disabled,
 * which is usually the case when the hook is being used outside the context of Statsig's
 * provider.
 *
 * @param {Pick<types.QueryHookFactoryOptions<U>, 'disableFeatureFlagUsage'>} options
 *   The options provided to the query hook factory, {@link createQueryHook}, from which to
 *   determine whether or not feature flag usage is disabled.
 */
const conditionalFeatureFlagHook = <U extends types.UrqlQueryHook>(
  options: Pick<types.QueryHookFactoryOptions<U>, 'disableFeatureFlagUsage'>,
) => (options.disableFeatureFlagUsage ? useDummyFeatureFlag : useHasFeatureFlag);

/**
 * Returns whether or not the created query hook should be paused due to the optionally provided
 * feature flag and its disabled/enabled state.
 *
 * The query should only be paused if the flag's enabled state is explicitly false.  This is
 * because a flag's state will be undefined if the 'featureFlag' option was not provided to the
 * query hook factory, {@link createQueryHook}, and will be {@link FeatureFlagUnsafe} if the
 * 'disableFeatureFlagUsage' option was set to true.
 */
const isQueryPausedDueToFeatureFlag = (flag: boolean | FeatureFlagUnsafe | undefined) =>
  flag === false;

/**
 * Creates a new query hook that wraps the original hook, {@link U}, that is imported directly
 * from the GraphQl generated types, for purposes of standardize error/success state handling,
 * logging and consistency.
 *
 * @param {U} hook
 *   The original query hook that is imported directly from the GraphQl generated types.
 * @param {O} config
 *   Configuration options that dictate how to construct the wrapped query hook.
 *
 *   @see {@link types.QueryHookFactoryOptions}
 *
 * @returns {WrappedQueryHook<U, O, UserReq>}
 *   A new query hook that wraps the original query hook, {@link U}, with configurable error
 *   handling, success handling and logging included.
 */
export const createQueryHook = <
  U extends types.UrqlQueryHook,
  O extends types.QueryHookFactoryOptions<U>,
>(
  hook: U,
  {
    featureFlag,
    issueToastNotifications = constants.DefaultUrqlIssueToastNotifications,
    requiresAuthenticatedUser = true,
    ...config
  }: Exact<U, O>,
): WrappedQueryHook<U, O> => {
  if (featureFlag !== undefined && config.disableFeatureFlagUsage === true) {
    throw new Error(
      "The 'featureFlag' option cannot be used when 'disableFeatureFlagUsage' is set to true.",
    );
  }
  const featureFlagHook = conditionalFeatureFlagHook<U>({
    disableFeatureFlagUsage: config.disableFeatureFlagUsage,
  });

  function useErrorState({
    error,
    isFetching,
    isStale,
  }: {
    readonly error: undefined | UrqlClientError;
    readonly isFetching: boolean;
    readonly isStale: boolean;
  }) {
    const logger = useLogger();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
      if (isStale || !error || isFetching) {
        return;
      }
      logger.error(error, error.__internalNonUserFacingMessage__, error.logContext);
      if (issueToastNotifications) {
        enqueueSnackbar(error.message, {
          variant: 'error',
        });
      }
    }, [enqueueSnackbar, logger, error, isStale, isFetching]);
  }

  function useSuccessState({
    data,
    error,
    state,
  }: {
    readonly data: types.QueryResponse<U, O> | undefined;
    readonly error: undefined | UrqlClientError;
    readonly state: UseQueryState<unknown, AnyVariables>;
  }) {
    const { enqueueSnackbar } = useSnackbar();

    const successMessage = useMemo(() => {
      if (typeof config.successMessage === 'function' && state.operation?.variables) {
        return config.successMessage(state.operation.variables as types.InferredQueryVariables<U>);
      } else if (typeof config.successMessage === 'string') {
        return config.successMessage;
      }
      return undefined;
    }, [state.operation?.variables]);

    useEffect(() => {
      if (
        !state.stale &&
        !error &&
        successMessage !== undefined &&
        data !== undefined &&
        issueToastNotifications
      ) {
        enqueueSnackbar(successMessage, {
          variant: 'success',
        });
      }
    }, [error, enqueueSnackbar, data, successMessage, state.stale]);
  }

  function useQuery(
    variables: types.InferredQueryVariables<U>,
    options?: Omit<types.InferredQueryOptions<U>, 'variables'>,
  ): types.WrappedQueryHookReturn<U, O> {
    const selectedRoleContext = useSelectedRole({ strict: requiresAuthenticatedUser });

    /* eslint-disable-next-line @eslint-react/exhaustive-deps */
    const context = useMemo(() => ({ ...config.context, ...options?.context }), []);

    const flag = featureFlagHook(featureFlag);

    const [state, execute] = hook({
      ...options,
      context,
      pause:
        Boolean(options?.pause) ||
        Boolean(config.pause?.(selectedRoleContext)) ||
        isQueryPausedDueToFeatureFlag(flag),
      requestPolicy: config.requestPolicy,
      variables,
    });

    const errorMessage = useMemo(() => {
      if (typeof config.errorMessage === 'function' && state.operation?.variables) {
        return config.errorMessage(state.operation.variables as types.InferredQueryVariables<U>);
      } else if (typeof config.errorMessage === 'string') {
        return config.errorMessage;
      }
      return undefined;
    }, [state.operation?.variables]);

    const error = useMemo(() => {
      if (state.error) {
        return new UrqlClientError(state.error, { userFacingMessage: errorMessage });
      }
      return undefined;
      /* `state` is intentionally omitted: it's only used as logging context inside
         parseUrqlHttpErrors, so a stale snapshot is acceptable and avoids re-firing
         the error toast on every render when the error hasn't changed. */
    }, [state.error, errorMessage]);

    const data = useMemo(() => {
      if (config.responseTransformer && state.data) {
        return config.responseTransformer(
          state.data as types.InferredOriginalQueryData<U>,
        ) as types.QueryResponse<U, O>;
      }
      return state.data as types.QueryResponse<U, O>;
    }, [state.data]);

    useErrorState({ error, isFetching: state.fetching, isStale: state.stale });
    useSuccessState({
      data,
      error,
      state,
    });

    const isNotAuthenticated = useMemo(
      () => (state.error ? isNotAuthenticatedError(state.error) : false),
      [state.error],
    );

    return {
      data,
      error,
      execute,
      isFetching: state.fetching,
      isNotAuthenticated,
    } as types.WrappedQueryHookReturn<U, O>;
  }

  return useQuery;
};
